import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountingSubNav } from '../components/AccountingSubNav';
import {
  CheckCircle2, ArrowDownLeft, ArrowUpRight, ArrowLeftRight,
  X, Loader2, Plus, Search, Filter, AlertCircle,
  Banknote,
} from 'lucide-react';
import { formatINR, formatINRPaise } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { localToday } from '@/shared/utils/utils';

// ── Shared styles ─────────────────────────────────────────────────────────────
const INPUT = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100 transition-colors';
const LABEL = 'block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5';

type Tab = 'receipt' | 'payment' | 'contra';

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    posted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    draft: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    submitted: 'bg-blue-100 text-blue-700',
    approved: 'bg-indigo-100 text-indigo-700',
    reversed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`uppercase text-[10px] px-2.5 py-0.5 rounded-full font-bold ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

// ── Customer Receipt Modal ────────────────────────────────────────────────────
function ReceiptModal({ accounts, initialPartyName = '', onClose, onPosted }: { accounts: any[]; initialPartyName?: string; onClose: () => void; onPosted: (status: 'posted' | 'submitted') => void }) {
  const [form, setForm] = useState({
    partyName: initialPartyName, cashBankAccountId: '', amount: '',
    date: localToday(), paymentMethod: 'bank_transfer',
    reference: '', narration: '',
  });
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState<any[]>([]);
  const [openInvoices, setOpenInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [appliedInvoiceId, setAppliedInvoiceId] = useState('');

  useEffect(() => {
    api.get('/api/accounting/parties?role=customer')
      .then(res => {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setParties(list);
      })
      .catch(() => {});
  }, []);

  // Matches the typed/selected customer name back to a party record so its
  // open invoices can be looked up. AccPartyModel and AccountsCustomerModel
  // share the same _id when a party was created as a customer (see
  // api-accounting/parties.js POST /), which is what lets this resolve.
  const matchedParty = parties.find(p => (p.displayName || p.legalName) === form.partyName);

  useEffect(() => {
    setAppliedInvoiceId('');
    if (!matchedParty?._id) { setOpenInvoices([]); return; }
    setInvoicesLoading(true);
    api.get(`/api/accounts/sales-invoices?customerId=${matchedParty._id}`)
      .then(res => {
        const list = Array.isArray(res.data.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setOpenInvoices(list.filter((inv: any) => ['unpaid', 'partially_paid', 'overdue'].includes(inv.status) && Number(inv.amountDue) > 0));
      })
      .catch(() => setOpenInvoices([]))
      .finally(() => setInvoicesLoading(false));
  }, [matchedParty?._id]);

  const applyInvoice = (invoiceId: string) => {
    setAppliedInvoiceId(invoiceId);
    const inv = openInvoices.find(i => i._id === invoiceId);
    if (!inv) return;
    setForm(f => ({
      ...f,
      amount: String(inv.amountDue),
      reference: f.reference || inv.invoiceNumber,
      narration: f.narration || `Against invoice ${inv.invoiceNumber}`,
    }));
  };

  const isCashBank = (a: any) =>
    !a.isGroupHeader &&
    a.allowManualPosting !== false &&
    (a.primaryGroup === 'Cash & Bank' ||
     a.subtype === 'cash' ||
     a.subtype === 'bank' ||
     ['1010', '1020', '1021', '1030'].includes(a.code) ||
     (a.code && a.code.startsWith('10') && !['1000', '1100', '1200', '1300', '1310', '1400', '1500', '1600', '1700'].includes(a.code)));

  const cashBankAccounts = accounts.filter(isCashBank);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountVal = parseFloat(form.amount);
    if (!amountVal || amountVal <= 0) { alert('Amount must be greater than zero.'); return; }
    setSaving(true);
    try {
      // RBM splits AR by customer channel (1210-1213) rather than one generic
      // code; this quick-entry form doesn't know the customer's channel, so
      // it falls back to the "Others" bucket — same default as the backend
      // (see accounting/coa/template-default.js's DEFAULT_AR_CODE).
      const arAccount = accounts.find(a => a.code === '1213');
      const lines = [
        { accountId: form.cashBankAccountId, debit: amountVal, credit: 0, description: `Receipt from ${form.partyName}` },
        { accountId: arAccount?._id || form.cashBankAccountId, contactId: form.partyName, debit: 0, credit: amountVal, description: `AR Cr — ${form.partyName}` },
      ];
      const r = await api.post('/api/accounting/journals', {
        journalDate: form.date,
        narration: form.narration || `Customer Receipt — ${form.partyName}`,
        reference: form.reference || `RCP-${Date.now()}`,
        sourceType: 'receipt',
        lines,
      });
      const id = r.data.data?._id;
      if (id) {
        // Submit only — do not self-approve. Segregation of duties requires a
        // second person to approve before this posts to the ledger; it will
        // show up in the Approval Inbox / this list with a "submitted" badge
        // until someone else does.
        await api.post(`/api/accounting/journals/${id}/submit`, {});
      }
      onPosted('submitted');
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-emerald-600">
          <div className="flex items-center gap-2 text-white">
            <ArrowDownLeft className="w-5 h-5" />
            <h2 className="text-base font-bold">Record Customer Receipt</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>Customer Name *</label>
              <input required className={INPUT} list="customer-list" placeholder="Select or type customer name..."
                value={form.partyName} onChange={e => setForm(f => ({ ...f, partyName: e.target.value }))} />
              <datalist id="customer-list">
                {parties.map(p => (
                  <option key={p._id} value={p.displayName || p.legalName}>
                    {p.contactId ? `${p.contactId} — ` : ''}{p.displayName || p.legalName}
                  </option>
                ))}
              </datalist>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Bank / Cash Account *</label>
              <select required className={INPUT} value={form.cashBankAccountId}
                onChange={e => setForm(f => ({ ...f, cashBankAccountId: e.target.value }))}>
                <option value="">Select account…</option>
                {cashBankAccounts.map(a => <option key={a._id} value={a._id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Amount (₹) *</label>
              <input required type="number" step="0.01" min="0.01" className={INPUT + ' font-mono'}
                placeholder="0.00" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Date *</label>
              <input required type="date" className={INPUT} value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Payment Method</label>
              <select className={INPUT} value={form.paymentMethod}
                onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                {['bank_transfer', 'cash', 'cheque', 'upi', 'card', 'neft', 'rtgs', 'imps'].map(m => (
                  <option key={m} value={m}>{m.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL}>Reference / UTR / Cheque No</label>
              <input className={INPUT} placeholder="Optional"
                value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Narration</label>
              <input className={INPUT} placeholder="Optional note"
                value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))} />
            </div>
          </div>

          {/* Open-invoice picker — filling the amount from a guess used to mean
              re-checking the customer's outstanding balance separately before
              posting. Picking an invoice here auto-fills the amount from its
              real amountDue; it only affects this receipt's numbers, it does
              not mark the invoice as paid (that write path is the Sales
              module's own Record Payment flow, RecordPaymentModal.tsx). */}
          {matchedParty && (
            <div>
              <label className={LABEL}>Apply to open invoice (optional)</label>
              {invoicesLoading ? (
                <p className="text-xs text-slate-400">Loading open invoices…</p>
              ) : openInvoices.length === 0 ? (
                <p className="text-xs text-slate-400">No open invoices for this customer.</p>
              ) : (
                <select className={INPUT} value={appliedInvoiceId} onChange={e => applyInvoice(e.target.value)}>
                  <option value="">Select an open invoice to auto-fill the amount…</option>
                  {openInvoices.map((inv: any) => (
                    <option key={inv._id} value={inv._id}>
                      {inv.invoiceNumber} — Due {formatINR(inv.amountDue)} (due {new Date(inv.dueDate).toLocaleDateString('en-IN')})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 text-xs text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900">
            <strong>Journal preview:</strong> Dr {form.cashBankAccountId ? accounts.find(a => a._id === form.cashBankAccountId)?.name || 'Cash/Bank' : 'Cash/Bank'} {formatINRPaise(Number(form.amount))} &nbsp;/&nbsp; Cr Accounts Receivable (1100) {formatINRPaise(Number(form.amount))}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Post Receipt
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Supplier Payment Modal ────────────────────────────────────────────────────
function PaymentModal({ accounts, onClose, onPosted }: { accounts: any[]; onClose: () => void; onPosted: (status: 'posted' | 'submitted') => void }) {
  const [form, setForm] = useState({
    partyName: '', cashBankAccountId: '', amount: '',
    date: localToday(), paymentMethod: 'bank_transfer',
    reference: '', narration: '',
  });
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [openBills, setOpenBills] = useState<any[]>([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [appliedBillId, setAppliedBillId] = useState('');

  useEffect(() => {
    api.get('/api/accounting/parties?role=supplier')
      .then(res => {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setSuppliers(list);
      })
      .catch(() => {});
  }, []);

  // Same _id-sharing relationship as the customer side (AccPartyModel /
  // AccountsVendorModel created together — see api-accounting/parties.js POST /).
  const matchedSupplier = suppliers.find(p => (p.displayName || p.legalName) === form.partyName);

  useEffect(() => {
    setAppliedBillId('');
    if (!matchedSupplier?._id) { setOpenBills([]); return; }
    setBillsLoading(true);
    api.get(`/api/accounts/purchase-bills?vendorId=${matchedSupplier._id}`)
      .then(res => {
        const list = Array.isArray(res.data.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setOpenBills(list.filter((bill: any) => ['unpaid', 'partially_paid', 'overdue'].includes(bill.status) && Number(bill.amountDue) > 0));
      })
      .catch(() => setOpenBills([]))
      .finally(() => setBillsLoading(false));
  }, [matchedSupplier?._id]);

  const applyBill = (billId: string) => {
    setAppliedBillId(billId);
    const bill = openBills.find(b => b._id === billId);
    if (!bill) return;
    setForm(f => ({
      ...f,
      amount: String(bill.amountDue),
      reference: f.reference || bill.vendorBillNumber || bill.billNumber,
      narration: f.narration || `Against bill ${bill.vendorBillNumber || bill.billNumber}`,
    }));
  };

  const cashBankAccounts = accounts.filter(a =>
    !a.isGroupHeader &&
    a.allowManualPosting !== false &&
    (a.primaryGroup === 'Cash & Bank' ||
     a.subtype === 'cash' ||
     a.subtype === 'bank' ||
     ['1010', '1020', '1021', '1030'].includes(a.code) ||
     (a.code && a.code.startsWith('10') && !['1000', '1100', '1200', '1300', '1310', '1400', '1500', '1600', '1700'].includes(a.code)))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountVal = parseFloat(form.amount);
    if (!amountVal || amountVal <= 0) { alert('Amount must be greater than zero.'); return; }
    setSaving(true);
    try {
      // Same reasoning as the AR fallback in ReceiptModal above.
      const apAccount = accounts.find(a => a.code === '2200');
      const lines = [
        { accountId: apAccount?._id || form.cashBankAccountId, contactId: form.partyName, debit: amountVal, credit: 0, description: `AP Dr — ${form.partyName}` },
        { accountId: form.cashBankAccountId, debit: 0, credit: amountVal, description: `Payment to ${form.partyName}` },
      ];
      const r = await api.post('/api/accounting/journals', {
        journalDate: form.date,
        narration: form.narration || `Supplier Payment — ${form.partyName}`,
        reference: form.reference || `PMT-${Date.now()}`,
        sourceType: 'payment',
        lines,
      });
      const id = r.data.data?._id;
      if (id) {
        // Submit only — see ReceiptModal's handleSubmit for why this no
        // longer self-approves.
        await api.post(`/api/accounting/journals/${id}/submit`, {});
      }
      onPosted('submitted');
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-indigo-600">
          <div className="flex items-center gap-2 text-white">
            <ArrowUpRight className="w-5 h-5" />
            <h2 className="text-base font-bold">Record Supplier Payment</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>Supplier Name *</label>
              <input required className={INPUT} list="supplier-list" placeholder="Select or type supplier name..."
                value={form.partyName} onChange={e => setForm(f => ({ ...f, partyName: e.target.value }))} />
              <datalist id="supplier-list">
                {suppliers.map(p => (
                  <option key={p._id} value={p.displayName || p.legalName}>
                    {p.contactId ? `${p.contactId} — ` : ''}{p.displayName || p.legalName}
                  </option>
                ))}
              </datalist>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Bank / Cash Account *</label>
              <select required className={INPUT} value={form.cashBankAccountId}
                onChange={e => setForm(f => ({ ...f, cashBankAccountId: e.target.value }))}>
                <option value="">Select account…</option>
                {cashBankAccounts.map(a => <option key={a._id} value={a._id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Amount (₹) *</label>
              <input required type="number" step="0.01" min="0.01" className={INPUT + ' font-mono'}
                placeholder="0.00" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Date *</label>
              <input required type="date" className={INPUT} value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Payment Method</label>
              <select className={INPUT} value={form.paymentMethod}
                onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                {['bank_transfer', 'cash', 'cheque', 'upi', 'neft', 'rtgs', 'imps'].map(m => (
                  <option key={m} value={m}>{m.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL}>Reference / UTR / Cheque No</label>
              <input className={INPUT} placeholder="Optional"
                value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Narration</label>
              <input className={INPUT} placeholder="Optional note"
                value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))} />
            </div>
          </div>

          {/* Open-bill picker — mirrors the customer-side invoice picker in
              ReceiptModal above. Auto-fills the amount from the bill's real
              amountDue; doesn't mark the bill as paid, that's a separate write
              path (PurchaseBillList.tsx / the Accounts module's own flow). */}
          {matchedSupplier && (
            <div>
              <label className={LABEL}>Apply to open bill (optional)</label>
              {billsLoading ? (
                <p className="text-xs text-slate-400">Loading open bills…</p>
              ) : openBills.length === 0 ? (
                <p className="text-xs text-slate-400">No open bills for this supplier.</p>
              ) : (
                <select className={INPUT} value={appliedBillId} onChange={e => applyBill(e.target.value)}>
                  <option value="">Select an open bill to auto-fill the amount…</option>
                  {openBills.map((bill: any) => (
                    <option key={bill._id} value={bill._id}>
                      {bill.vendorBillNumber || bill.billNumber} — Due {formatINR(bill.amountDue)} (due {new Date(bill.dueDate).toLocaleDateString('en-IN')})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-lg p-3 text-xs text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900">
            <strong>Journal preview:</strong> Dr Accounts Payable (2100) {formatINRPaise(Number(form.amount))} &nbsp;/&nbsp; Cr {form.cashBankAccountId ? accounts.find(a => a._id === form.cashBankAccountId)?.name || 'Cash/Bank' : 'Cash/Bank'} {formatINRPaise(Number(form.amount))}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Post Payment
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Contra Transfer Modal (Prompt 20) ─────────────────────────────────────────
function ContraModal({ accounts, onClose, onPosted }: { accounts: any[]; onClose: () => void; onPosted: (status: 'posted' | 'submitted') => void }) {
  const [form, setForm] = useState({
    sourceAccountId: '', destAccountId: '', amount: '',
    date: localToday(),
    bankCharges: '', useInTransit: false, narration: '',
  });
  const [saving, setSaving] = useState(false);
  const cashBankAccounts = accounts.filter(a =>
    !a.isGroupHeader &&
    a.allowManualPosting !== false &&
    (a.primaryGroup === 'Cash & Bank' ||
     a.subtype === 'cash' ||
     a.subtype === 'bank' ||
     ['1010', '1020', '1021', '1030'].includes(a.code) ||
     (a.code && a.code.startsWith('10') && !['1000', '1100', '1200', '1300', '1310', '1400', '1500', '1600', '1700'].includes(a.code)))
  );

  const sameAccount = form.sourceAccountId && form.sourceAccountId === form.destAccountId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sameAccount) { alert('Source and destination accounts must be different.'); return; }
    const amountVal = parseFloat(form.amount);
    const chargesVal = form.bankCharges ? parseFloat(form.bankCharges) : 0;
    if (!amountVal || amountVal <= 0) { alert('Amount must be greater than zero.'); return; }
    setSaving(true);
    try {
      // CT series: Dr Destination, Cr Source; optional Dr Bank Charges / Cr Source (charges)
      const lines: any[] = [
        { accountId: form.destAccountId, debit: amountVal, credit: 0, description: 'Contra — Destination' },
        { accountId: form.sourceAccountId, debit: 0, credit: amountVal + chargesVal, description: 'Contra — Source' },
      ];
      if (chargesVal > 0) {
        const chargesAccount = accounts.find(a => a.code === '6200' || a.name?.toLowerCase().includes('bank charge'));
        if (chargesAccount) {
          lines.push({ accountId: chargesAccount._id, debit: chargesVal, credit: 0, description: 'Bank Charges' });
        }
      }
      const r = await api.post('/api/accounting/journals', {
        journalDate: form.date,
        narration: form.narration || 'Contra Transfer',
        reference: `CT-${Date.now()}`,
        sourceType: 'contra',
        lines,
      });
      const id = r.data.data?._id;
      if (id) {
        // Submit only — see ReceiptModal's handleSubmit for why this no
        // longer self-approves.
        await api.post(`/api/accounting/journals/${id}/submit`, {});
      }
      onPosted('submitted');
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  const srcName = accounts.find(a => a._id === form.sourceAccountId)?.name || '—';
  const dstName = accounts.find(a => a._id === form.destAccountId)?.name || '—';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-amber-500">
          <div className="flex items-center gap-2 text-white">
            <ArrowLeftRight className="w-5 h-5" />
            <h2 className="text-base font-bold">Contra Transfer (CT)</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg px-3 py-2 text-xs text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-900">
            Contra transfers only move funds between cash/bank accounts. They never create income, expense, receivable, or payable entries.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>Source Account (Money Goes Out) *</label>
              <select required className={INPUT} value={form.sourceAccountId}
                onChange={e => setForm(f => ({ ...f, sourceAccountId: e.target.value }))}>
                <option value="">Select source…</option>
                {cashBankAccounts.map(a => <option key={a._id} value={a._id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Destination Account (Money Comes In) *</label>
              <select required className={INPUT} value={form.destAccountId}
                onChange={e => setForm(f => ({ ...f, destAccountId: e.target.value }))}>
                <option value="">Select destination…</option>
                {cashBankAccounts.map(a => <option key={a._id} value={a._id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            {sameAccount && (
              <div className="col-span-2 flex items-center gap-2 text-xs text-rose-600 bg-rose-50 dark:bg-rose-950/30 rounded-lg p-2.5 border border-rose-200">
                <AlertCircle className="w-4 h-4 shrink-0" /> Source and destination must be different accounts.
              </div>
            )}
            <div>
              <label className={LABEL}>Transfer Amount (₹) *</label>
              <input required type="number" step="0.01" min="0.01" className={INPUT + ' font-mono'}
                placeholder="0.00" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Date *</label>
              <input required type="date" className={INPUT} value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Bank Charges (₹) <span className="font-normal text-slate-400">Optional</span></label>
              <input type="number" step="0.01" min="0" className={INPUT + ' font-mono'}
                placeholder="0.00" value={form.bankCharges} onChange={e => setForm(f => ({ ...f, bankCharges: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Narration</label>
              <input className={INPUT} placeholder="e.g. Cash deposited to HDFC account"
                value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))} />
            </div>
          </div>

          {/* Journal Preview */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1.5 text-xs font-mono border border-slate-200 dark:border-slate-700">
            <div className="font-semibold text-slate-500 mb-1 font-sans">Journal Preview (CT Series)</div>
            <div className="flex justify-between"><span>Dr {dstName}</span><span className="text-emerald-600">{formatINRPaise(Number(form.amount))}</span></div>
            {form.bankCharges && Number(form.bankCharges) > 0 && <div className="flex justify-between"><span>Dr Bank Charges</span><span className="text-emerald-600">{formatINRPaise(Number(form.bankCharges))}</span></div>}
            <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-1.5"><span>Cr {srcName}</span><span className="text-rose-600">{formatINRPaise(Number(form.amount || 0) + Number(form.bankCharges || 0))}</span></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving || Boolean(sameAccount)} className="px-5 py-2 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Post Transfer
          </button>
        </div>
      </form>
    </div>
  );
}



// ── Main Page ─────────────────────────────────────────────────────────────────
export function VoucherListPage() {
  const [activeTab, setActiveTab] = useState<Tab>('receipt');
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<Tab | null>(null);
  const [prefillPartyName, setPrefillPartyName] = useState('');

  // Deep link from Client Profile's "Record Payment" button (?customerName=)
  // — used to just open this page with nothing prefilled, leaving the
  // salesperson to re-find and re-type the client's name themselves.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const customerName = searchParams.get('customerName');
    if (!customerName) return;
    setPrefillPartyName(customerName);
    setShowModal('receipt');
    const next = new URLSearchParams(searchParams);
    next.delete('customerName');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/accounting/journals?limit=100');
      const data = r.data;
      const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
      setVouchers(list);
    } catch { setVouchers([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get('/api/accounting/accounts?limit=200')
      .then(r => {
        const list = Array.isArray(r.data.data) ? r.data.data : r.data.data?.items || [];
        setAccounts(list);
      }).catch(() => {});
    fetchVouchers();
  }, [fetchVouchers]);

  const handlePosted = (label: string, status: 'posted' | 'submitted' = 'posted') => {
    setShowModal(null);
    showToast(status === 'submitted'
      ? `${label} submitted — awaiting a second person's approval before it posts to the ledger.`
      : `${label} posted successfully.`);
    fetchVouchers();
  };

  // Summary totals
  const receipts = vouchers.filter(v => v.sourceType === 'receipt');
  const payments = vouchers.filter(v => v.sourceType === 'payment');
  const contras = vouchers.filter(v => v.sourceType === 'contra');
  const totalIn = receipts.reduce((s, v) => s + (v.totalDebit || 0), 0);
  const totalOut = payments.reduce((s, v) => s + (v.totalDebit || 0), 0);

  const TABS: { id: Tab; label: string; icon: React.ElementType; count: number; color: string }[] = [
    { id: 'receipt', label: 'Customer Receipts', icon: ArrowDownLeft, count: receipts.length, color: 'emerald' },
    { id: 'payment', label: 'Supplier Payments', icon: ArrowUpRight, count: payments.length, color: 'indigo' },
    { id: 'contra', label: 'Contra Transfers', icon: ArrowLeftRight, count: contras.length, color: 'amber' },
  ];

  const filteredVouchers = vouchers.filter(v => {
    const matchTab = activeTab === 'receipt' ? v.sourceType === 'receipt' :
      activeTab === 'payment' ? v.sourceType === 'payment' :
      v.sourceType === 'contra';
    const q = search.toLowerCase();
    const matchSearch = !q || (v.narration || '').toLowerCase().includes(q) ||
      (v.journalNo || '').toLowerCase().includes(q) ||
      (v.reference || '').toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  return (
    <div className="">
      <AccountingSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">

        {/* Toast */}
        {toast && (
          <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-sm shadow-sm animate-in fade-in">
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" /> {toast}
          </div>
        )}

        <PageHeader
          icon={Banknote}
          eyebrow="Accounts"
          title="Receipts & Payments"
          description="Customer receipts, supplier payments and cash/bank contra transfers — each allocated to its subledger."
          actions={<>
            <Button variant="outline" onClick={() => setShowModal('contra')}>
              <ArrowLeftRight className="h-4 w-4" /> Contra
            </Button>
            <Button variant="outline" onClick={() => setShowModal('payment')}>
              <Plus className="h-4 w-4" /> Payment
            </Button>
            <Button onClick={() => setShowModal('receipt')}>
              <Plus className="h-4 w-4" /> Receipt
            </Button>
          </>}
        />

        <StatGrid cols={3}>
          <StatCard label="Money In" value={formatINR(totalIn / 100)} hint={`${receipts.length} receipt${receipts.length !== 1 ? 's' : ''}`} icon={ArrowDownLeft} tone="success" />
          <StatCard label="Money Out" value={formatINR(totalOut / 100)} hint={`${payments.length} payment${payments.length !== 1 ? 's' : ''}`} icon={ArrowUpRight} tone="primary" />
          <StatCard label="Contra Transfers" value={contras.length} hint="Cash / bank transfers" icon={ArrowLeftRight} tone="warning" />
        </StatGrid>

        {/* Tab Bar + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 pb-0">
          <div className="flex gap-1">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const colorMap: Record<string, string> = {
                emerald: 'border-emerald-600 text-emerald-600 dark:text-emerald-400 dark:border-emerald-400',
                indigo: 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400',
                amber: 'border-amber-500 text-amber-600 dark:text-amber-400 dark:border-amber-400',
              };
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
                    isActive ? colorMap[tab.color] + ' font-semibold' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ml-0.5 ${
                    isActive ? 'bg-primary/10 text-primary' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                  }`}>{tab.count}</span>
                </button>
              );
            })}
          </div>
          <div className="relative pb-0 mb-1">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-60"
              placeholder="Search narration, journal no…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
          ) : filteredVouchers.length === 0 ? (
            <div className="py-16 text-center">
              <Filter className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No {activeTab} entries yet.</p>
              <button
                onClick={() => setShowModal(activeTab)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:underline font-semibold"
              >
                <Plus className="w-3.5 h-3.5" /> Create the first {activeTab}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    {['Date', 'Journal No', 'Narration / Reference', 'Amount (₹)', 'Status'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredVouchers.map(v => (
                    <tr key={v._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors cursor-pointer">
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {v.journalDate ? new Date(v.journalDate).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-indigo-700 dark:text-indigo-400 text-xs">
                        {v.journalNo || 'DRAFT'}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{v.narration || '—'}</p>
                        {v.reference && <p className="text-xs text-slate-400 font-mono mt-0.5">{v.reference}</p>}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-900 dark:text-slate-100">
                        {v.totalDebit != null ? formatINR((v.totalDebit || 0) / 100) : '—'}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={v.status || 'posted'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal === 'receipt' && (
        <ReceiptModal accounts={accounts} initialPartyName={prefillPartyName} onClose={() => { setShowModal(null); setPrefillPartyName(''); }} onPosted={(status) => handlePosted('Customer Receipt', status)} />
      )}
      {showModal === 'payment' && (
        <PaymentModal accounts={accounts} onClose={() => setShowModal(null)} onPosted={(status) => handlePosted('Supplier Payment', status)} />
      )}
      {showModal === 'contra' && (
        <ContraModal accounts={accounts} onClose={() => setShowModal(null)} onPosted={(status) => handlePosted('Contra Transfer', status)} />
      )}
    </div>
  );
}

export default VoucherListPage;
